import { RequestStatus } from '@magimp/shared';
import { nextClientPackageId } from './draft-ids.js';
import type { DraftScratchpad, UserRef } from './types.js';

export function buildPersistedDraftScratchpad(
  row: DraftScratchpad & { id: string },
  manager: UserRef,
): DraftScratchpad {
  const seeded: DraftScratchpad = {
    manager,
    name: row.name,
    // WHY (#701): hydrate persisted kind / transportTypeId so a
    //   reopened CONTAINER draft keeps its discriminator.
    kind: row.kind ?? 'STANDARD',
    transportTypeId: row.transportTypeId ?? null,
    route: { from: row.route.from, to: row.route.to },
    // WHY (#700): hydrate destinations from the persisted row so a
    //   re-opened draft round-trips the same multiselect state.
    destinations: row.destinations.map((d) => ({
      arrivalCityId: d.arrivalCityId,
      sortOrder: d.sortOrder,
    })),
    // WHY (#1057): hydrate the persisted deliveryAddress so a
    //   re-opened CONTAINER draft renders the existing typed value.
    //   STANDARD rows always carry `null` (the wire forbids non-null
    //   here); the editor's STANDARD branch never reads the slot.
    deliveryAddress: row.deliveryAddress ?? null,
    // WHY (#1393): hydrate the persisted pickupAddress so a re-opened
    //   CONTAINER draft renders the existing typed value. Non-CONTAINER
    //   rows always carry `null` (the wire forbids non-null here).
    pickupAddress: row.pickupAddress ?? null,
    // WHY (#1061): hydrate the persisted insurance triplet so a
    //   re-opened draft round-trips the manager's prior choice.
    //   Legacy/pre-#1052 fixtures default missing keys to the
    //   "not requested" stance.
    insuranceRequested: row.insuranceRequested ?? false,
    insuranceCargoValue: row.insuranceCargoValue ?? null,
    insuranceCargoCurrency: row.insuranceCargoCurrency ?? null,
    // WHY (#996): hydrate Roads/Packaging selections from the persisted
    //   row so a reopened draft round-trips the existing IDs without
    //   the user re-picking. The seeded list MAY include inactive
    //   IDs (historical selections); the editor preserves them on
    //   save unless the user removes them explicitly.
    roadOptionIds: row.roadOptionIds ?? [],
    packagingOptionIds: row.packagingOptionIds ?? [],
    // WHY (#1448): hydrate the STANDARD request-level totals so a reopened
    //   draft renders the persisted weight/volume. A legacy STANDARD row
    //   that predates the #1447 backfill carries `null` and the gate then
    //   blocks Send until the manager fills them.
    totalWeight: row.totalWeight,
    totalVolume: row.totalVolume,
    items: row.items.map((it) => ({
      id: it.id,
      name: it.name,
      brand: it.brand,
      // WHY (#1055): material round-trips through the draft store as
      //   a non-null string. Wire-side optional/absent → ''.
      material: it.material ?? '',
      weight: it.weight,
      volume: it.volume,
      quantity: it.quantity,
      // WHY (#1056): wire is nullable+optional → coalesce `undefined`
      //   from legacy bundles to `null` so the draft slot has one
      //   sentinel.
      productQuantity: it.productQuantity ?? null,
      description: it.description,
      photos: [...it.photos],
      // WHY (#590): only present on opt-in detail fetches. Treat
      //   absence as legacy/manual mode with no package rows.
      dimensionsMode: it.dimensionsMode ?? false,
      packages: (it.packages ?? []).map((p) => ({
        id: p.id,
        clientPackageId: nextClientPackageId(),
        widthCm: p.widthCm,
        depthCm: p.depthCm,
        heightCm: p.heightCm,
        weightKg: p.weightKg,
        count: p.count,
      })),
    })),
    // WHY (#599): caller seeds `boxes` from a `?includeBoxes=true`
    //   detail fetch. When the field is absent (legacy fetch), pass
    //   `[]` and `boxesSectionOpen=false` so the editor never sends
    //   `boxes` on the next save and persisted boxes round-trip
    //   intact. The reopen call site is responsible for flipping
    //   `boxesSectionOpen=true` when seeding from a detail with
    //   any persisted boxes.
    boxes: row.boxes,
    boxesSectionOpen: row.boxesSectionOpen,
    clientToken: crypto.randomUUID(),
    // INVARIANT (#536): editor-side projection of the row's actual
    //   status. The save endpoint preserves it server-side; this slot
    //   is for status-aware UI copy and the read-only return-reason
    //   banner only.
    status: row.status,
    returnReason: row.status === RequestStatus.RETURN ? row.returnReason : null,
  };
  return seeded;
}
